import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import { Alert, Button, Card, SelectField, TextAreaField, TextField } from '@mrigl/ui';
import { Star } from 'lucide-react';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import {
  GridBody,
  GridToolbar,
  Pagination,
  SearchFilter,
  SortHeader,
  useGridState,
  usePagedData,
  useRowLink,
  type ListQuery,
} from '../../grid.js';
import { useI18n, type I18n } from '../../i18n.js';
import { display, useStore, type StoreDetails } from '../catalog/shared.js';

type Review = Schemas['AdminReview'];
type Summary = Schemas['ReviewSummary'];
type Rating = Schemas['RatingSummary'];
type Comment = Schemas['StoreComment'];
type ReviewsQuery = ListQuery<'/admin-api/v1/stores/{storeId}/reviews'>;
type CommentsQuery = ListQuery<'/admin-api/v1/stores/{storeId}/comments'>;

const STATUSES: readonly Review['status'][] = ['pending', 'approved', 'hidden'];
const SUBJECTS: readonly Review['subject'][] = ['product', 'store'];

/** Five small stars for a rating (the text next to it says the value). */
export function RatingStars({
  rating,
  label,
}: {
  readonly rating: number;
  readonly label: string;
}) {
  return (
    <span className="admin-stars" role="img" aria-label={label}>
      {[1, 2, 3, 4, 5].map((position) => (
        <Star
          key={position}
          size={14}
          aria-hidden="true"
          className={position <= rating ? 'admin-stars__on' : 'admin-stars__off'}
        />
      ))}
    </span>
  );
}

function subjectName(review: Review, locale: I18n['locale'], store: StoreDetails, t: I18n['t']) {
  return review.product === null
    ? t('admin.reviews.subject.store')
    : display(review.product.title, locale, store, review.product.handle);
}

function RatingCard({
  title,
  rating,
  t,
  formatNumber,
}: {
  readonly title: string;
  readonly rating: Rating;
  readonly t: I18n['t'];
  readonly formatNumber: (value: number) => string;
}) {
  return (
    <div className="admin-kpi">
      <span className="mrigl-muted">{title}</span>
      {rating.average === null ? (
        <span className="mrigl-muted">{t('admin.reviews.summary.none')}</span>
      ) : (
        <>
          <span className="admin-kpi__value">
            {formatNumber(rating.average)}
            <span className="admin-kpi__unit">/5</span>
          </span>
          <span className="mrigl-muted">
            {t('admin.reviews.summary.average', {
              average: formatNumber(rating.average),
              count: rating.count,
            })}
          </span>
        </>
      )}
    </div>
  );
}

/** Reviews of the store and its products: moderation queue and history. */
export function ReviewsPage() {
  const { storeId = '' } = useParams();
  const { t, locale, formatDateTime } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [summary, setSummary] = useState<Summary | null>(null);
  const grid = useGridState({
    sort: '-createdAt',
    sortable: ['createdAt', 'rating'],
    filters: { q: '', status: '', subject: '', rating: '' },
  });
  const rowLink = useRowLink();
  const { filters } = grid;
  const key = JSON.stringify([storeId, grid.page, grid.pageSize, grid.sort, filters]);
  const { data, loading, error } = usePagedData<Review>(
    key,
    () =>
      unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/reviews', {
          params: {
            path: { storeId },
            query: {
              page: grid.page,
              pageSize: grid.pageSize,
              sort: grid.sort as NonNullable<ReviewsQuery['sort']>,
              ...(filters.q === '' ? {} : { q: filters.q }),
              ...(filters.status === '' ? {} : { status: filters.status as Review['status'] }),
              ...(filters.subject === '' ? {} : { subject: filters.subject as Review['subject'] }),
              ...(filters.rating === '' ? {} : { rating: Number(filters.rating) }),
            },
          },
        }),
      ),
    grid.setPage,
  );

  useEffect(() => {
    void unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/reviews/summary', {
        params: { path: { storeId } },
      }),
    )
      .then(setSummary)
      .catch(() => {
        setSummary(null);
      });
  }, [storeId]);

  if (storeError !== null) return <Alert tone="error">{storeError}</Alert>;
  if (store === null) return <Loading />;
  const formatNumber = (value: number) =>
    new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
      value,
    );
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <div className="admin-title-row">
        <h1 className="mrigl-heading">{t('admin.reviews.title')}</h1>
        {summary === null || summary.pending === 0 ? null : (
          <button
            type="button"
            className="mrigl-button mrigl-button--secondary"
            onClick={() => {
              grid.setFilter('status', 'pending');
            }}
          >
            {t('admin.reviews.pending', { count: summary.pending })}
          </button>
        )}
      </div>
      {summary === null ? null : (
        <div className="admin-kpis">
          <RatingCard
            title={t('admin.reviews.summary.store')}
            rating={summary.store}
            t={t}
            formatNumber={formatNumber}
          />
          <RatingCard
            title={t('admin.reviews.summary.products')}
            rating={summary.products}
            t={t}
            formatNumber={formatNumber}
          />
        </div>
      )}
      <GridToolbar
        activeFilters={grid.activeFilters}
        onReset={grid.reset}
        search={
          <SearchFilter
            label={t('admin.reviews.search')}
            value={filters.q}
            onChange={(value) => {
              grid.setFilter('q', value);
            }}
          />
        }
      >
        <SelectField
          label={t('admin.reviews.status')}
          value={filters.status}
          onChange={(event) => {
            grid.setFilter('status', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.reviews.filter.allStatuses') },
            ...STATUSES.map((value) => ({ value, label: t(`admin.reviews.status.${value}`) })),
          ]}
        />
        <SelectField
          label={t('admin.reviews.subject')}
          value={filters.subject}
          onChange={(event) => {
            grid.setFilter('subject', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.reviews.filter.allSubjects') },
            ...SUBJECTS.map((value) => ({ value, label: t(`admin.reviews.subject.${value}`) })),
          ]}
        />
        <SelectField
          label={t('admin.reviews.rating')}
          value={filters.rating}
          onChange={(event) => {
            grid.setFilter('rating', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.reviews.filter.allRatings') },
            ...[5, 4, 3, 2, 1].map((value) => ({
              value: String(value),
              label: t('admin.reviews.stars', { rating: value }),
            })),
          ]}
        />
      </GridToolbar>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {data === null ? (
        loading ? (
          <Loading />
        ) : null
      ) : (
        <>
          <GridBody
            loading={loading}
            empty={data.items.length === 0}
            filtered={grid.activeFilters > 0}
            emptyText={t('admin.reviews.empty')}
          >
            <table className="mrigl-table">
              <thead>
                <tr>
                  <SortHeader
                    field="createdAt"
                    label={t('admin.reviews.column.date')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                    firstDescending
                  />
                  <SortHeader
                    field="rating"
                    label={t('admin.reviews.rating')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                    firstDescending
                  />
                  <th scope="col">{t('admin.reviews.column.review')}</th>
                  <th scope="col">{t('admin.reviews.subject')}</th>
                  <th scope="col">{t('admin.reviews.column.author')}</th>
                  <th scope="col">{t('admin.reviews.status')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((review) => {
                  const to = `/stores/${storeId}/reviews/${review.reviewId}`;
                  return (
                    <tr key={review.reviewId} {...rowLink(to)}>
                      <td className="admin-nowrap">
                        <Link
                          className="admin-cell-link"
                          to={to}
                          aria-label={t('admin.reviews.open', {
                            date: formatDateTime(review.createdAt),
                          })}
                        >
                          {formatDateTime(review.createdAt)}
                        </Link>
                      </td>
                      <td>
                        <RatingStars
                          rating={review.rating}
                          label={t('admin.reviews.stars', { rating: review.rating })}
                        />
                      </td>
                      <td className="admin-review-cell" dir="auto">
                        {review.comment ?? (
                          <span className="mrigl-muted">{t('admin.reviews.noComment')}</span>
                        )}
                      </td>
                      <td dir="auto">{subjectName(review, locale, store, t)}</td>
                      <td dir="auto">{review.authorName ?? t('admin.reviews.anonymous')}</td>
                      <td>
                        <span className={`admin-status admin-status--review-${review.status}`}>
                          {t(`admin.reviews.status.${review.status}`)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </GridBody>
          <Pagination
            page={grid.page}
            pageSize={grid.pageSize}
            total={data.total}
            onPage={grid.setPage}
            onPageSize={grid.setPageSize}
          />
        </>
      )}
    </div>
  );
}

function reviewError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError && failure.code === 'version-conflict') {
    return t('admin.reviews.error.version-conflict');
  }
  return errorMessage(t, failure);
}

/** One review: publish or hide it, and write the public reply. */
export function ReviewPage() {
  const { storeId = '', reviewId = '' } = useParams();
  const { t, locale, formatDateTime } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [review, setReview] = useState<Review | null>(null);
  const [reply, setReply] = useState('');
  const [status, setStatus] = useState<{ tone: 'error' | 'success'; message: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const loaded = await unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/reviews/{reviewId}', {
          params: { path: { storeId, reviewId } },
        }),
      );
      setReview(loaded);
      setReply(loaded.reply ?? '');
    } catch (failure) {
      setStatus({ tone: 'error', message: errorMessage(t, failure) });
    }
  }, [storeId, reviewId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function change(body: { status?: 'approved' | 'hidden'; reply?: string | null }) {
    if (review === null) return;
    setBusy(true);
    setStatus(null);
    try {
      const updated = await unwrap(
        api.PATCH('/admin-api/v1/stores/{storeId}/reviews/{reviewId}', {
          params: { path: { storeId, reviewId } },
          body: { ...body, version: review.version },
        }),
      );
      setReview(updated);
      setReply(updated.reply ?? '');
      setStatus({ tone: 'success', message: t('admin.reviews.saved') });
    } catch (failure) {
      setStatus({ tone: 'error', message: reviewError(t, failure) });
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (storeError !== null) return <Alert tone="error">{storeError}</Alert>;
  if (store === null || (review === null && status === null)) return <Loading />;
  const canManage = store.permissions.includes('reviews.manage');
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}/reviews`}>{t('admin.reviews.title')}</Link>
      </p>
      {status === null ? null : <Alert tone={status.tone}>{status.message}</Alert>}
      {review === null ? null : (
        <>
          <div className="admin-title-row">
            <h1 className="mrigl-heading" dir="auto">
              {subjectName(review, locale, store, t)}
            </h1>
            <span className={`admin-status admin-status--review-${review.status}`}>
              {t(`admin.reviews.status.${review.status}`)}
            </span>
          </div>
          <Card>
            <blockquote className="admin-review-quote">
              <div className="admin-review-head">
                <RatingStars
                  rating={review.rating}
                  label={t('admin.reviews.stars', { rating: review.rating })}
                />
                <strong dir="auto">{review.authorName ?? t('admin.reviews.anonymous')}</strong>
                <time className="mrigl-muted admin-review-date" dateTime={review.createdAt}>
                  {formatDateTime(review.createdAt)}
                </time>
              </div>
              <p className="admin-review-text" dir="auto">
                {review.comment ?? t('admin.reviews.noComment')}
              </p>
            </blockquote>
            <dl className="admin-details">
              <div className="admin-details__row">
                <dt>{t('admin.order.column.number')}</dt>
                <dd>
                  <Link to={`/stores/${storeId}/orders/${review.order.orderId}`}>
                    {t('admin.reviews.order', { number: review.order.number })}
                  </Link>
                </dd>
              </div>
              <div className="admin-details__row">
                <dt>{t('admin.reviews.language')}</dt>
                <dd>{t(`admin.language.${review.locale}`)}</dd>
              </div>
            </dl>
            {canManage ? (
              <div className="admin-actions">
                {review.status === 'approved' ? null : (
                  <Button
                    busy={busy}
                    onClick={() => {
                      void change({ status: 'approved' });
                    }}
                  >
                    {t('admin.reviews.approve')}
                  </Button>
                )}
                {review.status === 'hidden' ? null : (
                  <Button
                    variant="secondary"
                    busy={busy}
                    onClick={() => {
                      void change({ status: 'hidden' });
                    }}
                  >
                    {t('admin.reviews.hide')}
                  </Button>
                )}
              </div>
            ) : null}
          </Card>
          <Card labelledBy="review-reply">
            <h2 id="review-reply" className="mrigl-subheading">
              {t('admin.reviews.reply')}
            </h2>
            {canManage ? (
              <form
                className="mrigl-stack"
                onSubmit={(event: SubmitEvent) => {
                  event.preventDefault();
                  void change({ reply: reply.trim() === '' ? null : reply.trim() });
                }}
              >
                <TextAreaField
                  label={t('admin.reviews.replyLabel')}
                  hint={t('admin.reviews.replyHint')}
                  value={reply}
                  maxLength={2000}
                  rows={4}
                  onChange={(event) => {
                    setReply(event.target.value);
                  }}
                />
                <div className="admin-actions">
                  <Button type="submit" busy={busy}>
                    {t('admin.reviews.saveReply')}
                  </Button>
                  {review.reply === null ? null : (
                    <Button
                      variant="secondary"
                      busy={busy}
                      onClick={() => {
                        void change({ reply: null });
                      }}
                    >
                      {t('admin.reviews.removeReply')}
                    </Button>
                  )}
                </div>
              </form>
            ) : (
              <p dir="auto">{review.reply ?? '—'}</p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

/** Private comments left by visitors: read, mark handled. */
export function CommentsPage() {
  const { storeId = '' } = useParams();
  const { t, locale, formatDateTime } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [actionError, setActionError] = useState<string | null>(null);
  const grid = useGridState({
    sort: '-createdAt',
    sortable: ['createdAt'],
    filters: { q: '', status: 'new', from: '', to: '' },
  });
  const { filters } = grid;
  const key = JSON.stringify([storeId, grid.page, grid.pageSize, grid.sort, filters]);
  const { data, loading, error, reload } = usePagedData<Comment>(
    key,
    () =>
      unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/comments', {
          params: {
            path: { storeId },
            query: {
              page: grid.page,
              pageSize: grid.pageSize,
              sort: grid.sort as NonNullable<CommentsQuery['sort']>,
              ...(filters.q === '' ? {} : { q: filters.q }),
              ...(filters.status === '' ? {} : { status: filters.status as Comment['status'] }),
              ...(filters.from === '' ? {} : { from: filters.from }),
              ...(filters.to === '' ? {} : { to: filters.to }),
            },
          },
        }),
      ),
    grid.setPage,
  );

  async function setCommentStatus(comment: Comment, status: Comment['status']) {
    setActionError(null);
    try {
      await unwrap(
        api.PATCH('/admin-api/v1/stores/{storeId}/comments/{commentId}', {
          params: { path: { storeId, commentId: comment.commentId } },
          body: { status },
        }),
      );
      reload();
    } catch (failure) {
      setActionError(errorMessage(t, failure));
    }
  }

  if (storeError !== null) return <Alert tone="error">{storeError}</Alert>;
  if (store === null) return <Loading />;
  const canManage = store.permissions.includes('reviews.manage');
  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.comments.title')}</h1>
      <p className="mrigl-muted">{t('admin.comments.intro')}</p>
      <GridToolbar
        activeFilters={grid.activeFilters}
        onReset={grid.reset}
        search={
          <SearchFilter
            label={t('admin.comments.search')}
            value={filters.q}
            onChange={(value) => {
              grid.setFilter('q', value);
            }}
          />
        }
      >
        <SelectField
          label={t('admin.comments.status')}
          value={filters.status}
          onChange={(event) => {
            grid.setFilter('status', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.comments.filter.allStatuses') },
            { value: 'new', label: t('admin.comments.status.new') },
            { value: 'handled', label: t('admin.comments.status.handled') },
          ]}
        />
        <TextField
          type="date"
          label={t('admin.grid.from')}
          value={filters.from}
          onChange={(event) => {
            grid.setFilter('from', event.target.value);
          }}
        />
        <TextField
          type="date"
          label={t('admin.grid.to')}
          value={filters.to}
          onChange={(event) => {
            grid.setFilter('to', event.target.value);
          }}
        />
      </GridToolbar>
      {error === null && actionError === null ? null : (
        <Alert tone="error">{actionError ?? error}</Alert>
      )}
      {data === null ? (
        loading ? (
          <Loading />
        ) : null
      ) : (
        <>
          <GridBody
            loading={loading}
            empty={data.items.length === 0}
            filtered={grid.activeFilters > 0}
            emptyText={t('admin.comments.empty')}
          >
            <table className="mrigl-table">
              <thead>
                <tr>
                  <SortHeader
                    field="createdAt"
                    label={t('admin.reviews.column.date')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                    firstDescending
                  />
                  <th scope="col">{t('admin.comments.column.message')}</th>
                  <th scope="col">{t('admin.reviews.language')}</th>
                  <th scope="col">{t('admin.comments.status')}</th>
                  {canManage ? (
                    <th scope="col">
                      <span className="mrigl-visually-hidden">{t('admin.comments.status')}</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {data.items.map((comment) => (
                  <tr key={comment.commentId}>
                    <td className="admin-nowrap">{formatDateTime(comment.createdAt)}</td>
                    <td className="admin-comment-cell" dir="auto">
                      {comment.message}
                    </td>
                    <td>{t(`admin.language.${comment.locale}`)}</td>
                    <td>
                      <span className={`admin-status admin-status--comment-${comment.status}`}>
                        {t(`admin.comments.status.${comment.status}`)}
                      </span>
                    </td>
                    {canManage ? (
                      <td className="admin-nowrap">
                        <Button
                          variant="secondary"
                          onClick={() => {
                            void setCommentStatus(
                              comment,
                              comment.status === 'new' ? 'handled' : 'new',
                            );
                          }}
                        >
                          {comment.status === 'new'
                            ? t('admin.comments.markHandled')
                            : t('admin.comments.reopen')}
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </GridBody>
          <Pagination
            page={grid.page}
            pageSize={grid.pageSize}
            total={data.total}
            onPage={grid.setPage}
            onPageSize={grid.setPageSize}
          />
        </>
      )}
    </div>
  );
}
